import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import { Toast, ToastContainer } from "../../../components/CustomUI/Toast";
import {
  FiShield,
  FiArrowRight,
  FiArrowLeft,
  FiSave,
  FiCheckCircle,
} from "react-icons/fi";
import {
  PageAccessSelector,
  ApprovalAccessSelector,
  getReadPaths,
  hasPageWrite,
} from "./PageAccessSelector";
import { APPROVES_PAGE_PATH } from "../../../config/approvalAccess";
import { useTitle } from "../../../hooks/useTitle";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const emptyProfile = {
  name: "",
  description: "",
  pages: [],
  machineAccess: [],
  approvalAccess: [],
  fullAccess: false,
};

const ProfileForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getToken } = useAuth();

  // Try to get tenantId from state (if admin navigated from index page)
  const tenantId = location.state?.tenantId || "";

  const getAxiosConfig = () => {
    const config = {
      headers: { Authorization: `Bearer ${getToken()}` },
      params: {},
    };
    if (tenantId) {
      config.params.tenantId = tenantId;
    }
    return config;
  };

  useTitle(`Rovion MES - ${isEditing ? "Profil Düzenle" : "Profil Ekle"}`);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [profileData, setProfileData] = useState({ ...emptyProfile });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);

  const addToast = (title, description, appearance = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, title, description, appearance }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  useEffect(() => {
    if (isEditing) {
      fetchProfile();
    }
  }, [id]);

  const fetchProfile = async () => {
    try {
      const config = getAxiosConfig();
      if (tenantId) config.params.tenantId = tenantId;
      const res = await axios.get(`${API_URL}/api/user-profiles/${id}`, config);
      if (res.data.success) {
        const data = res.data.profile;
        setProfileData({
          ...emptyProfile,
          ...data,
          pages: data?.pages || [],
          approvalAccess: data?.approvalAccess || [],
        });
      } else {
        addToast("Hata", "Profil yüklenemedi", "danger");
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
      addToast("Hata", "Profil yüklenirken bir hata oluştu.", "danger");
    } finally {
      setLoading(false);
    }
  };

  const hasApprovesPageAccess = (profileData.pages || []).some((token) => {
    const base = token.split(":")[0];
    return base === APPROVES_PAGE_PATH;
  });

  const steps = [{ id: "general", label: "Genel Bilgiler" }];

  if (!profileData.fullAccess && !profileData.isSystem) {
    steps.push({ id: "pages", label: "Sayfa Yetkileri" });
  }

  if (
    hasApprovesPageAccess &&
    !profileData.fullAccess &&
    !profileData.isSystem
  ) {
    steps.push({ id: "approvals", label: "Onay Yetkileri" });
  }
  steps.push({ id: "summary", label: "Özet ve Kaydet" });

  const readPageCount = getReadPaths(profileData.pages).length;
  const writePageCount = getReadPaths(profileData.pages).filter((p) =>
    hasPageWrite(profileData.pages, p),
  ).length;

  const handleNext = () => {
    if (currentStepIndex === 0 && !profileData.name.trim()) {
      addToast("Uyarı", "Lütfen profil adını giriniz.", "warning");
      return;
    }

    const currentStepId = steps[currentStepIndex].id;

    if (currentStepId === "pages") {
      if (readPageCount === 0) {
        addToast("Uyarı", "En az bir sayfa yetkisi seçmelisiniz.", "warning");
        return;
      }
    }

    setCurrentStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handlePrev = () => {
    setCurrentStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSave = async () => {
    if (!profileData.name.trim()) {
      addToast("Uyarı", "Lütfen profil adını giriniz.", "warning");
      return;
    }

    setSaving(true);
    try {
      const config = getAxiosConfig();
      if (tenantId) config.params.tenantId = tenantId;

      const payload = {
        name: profileData.name,
        description: profileData.description,
        pages: profileData.pages || [],
        approvalAccess: profileData.approvalAccess || [],
        fullAccess: profileData.fullAccess,
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/user-profiles/${id}`, payload, config);
        addToast("Başarılı", "Profil başarıyla güncellendi.");
      } else {
        await axios.post(`${API_URL}/api/user-profiles`, payload, config);
        addToast("Başarılı", "Profil başarıyla oluşturuldu.");
      }

      setTimeout(() => navigate("/user-profiles"), 1500);
    } catch (err) {
      console.error("Save error:", err);
      const msg = err.response?.data?.message || "Kayıt işlemi başarısız oldu.";
      addToast("Hata", msg, "danger");
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: "Ana Sayfa", path: "/" },
    { label: "Kullanıcı Yönetimi" },
    { label: "Kullanıcı Profilleri", path: "/user-profiles" },
    { label: isEditing ? "Profil Düzenle" : "Profil Ekle" },
  ];

  return (
    <AppPage breadcrumbs={breadcrumbs} loading={loading}>
      <Container>
        <ContainerItem>
          <div className="d-flex align-items-center justify-content-between mb-4 card card-body">
            <h2 className="mb-0">
              {isEditing ? "Profil Düzenle" : "Yeni Profil Ekle"}
            </h2>
            <Button
              appearance="danger-subtle"
              onClick={() => navigate("/user-profiles")}
            >
              İptal
            </Button>
          </div>

          <div
            className="card shadow-sm border border-light mb-4"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            {/* Stepper Header */}
            <div className="bg-white border-bottom px-4 py-4">
              <div
                className="d-flex align-items-center justify-content-center position-relative w-100"
                style={{ margin: "0 auto" }}
              >
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <div
                      className="d-flex flex-column align-items-center position-relative z-1"
                      style={{ width: "100px" }}
                    >
                      <div
                        className={`d-flex align-items-center justify-content-center rounded-circle mb-2 transition-all`}
                        style={{
                          width: "44px",
                          height: "44px",
                          backgroundColor:
                            currentStepIndex >= index
                              ? "var(--m-primary)"
                              : "#fff",
                          color: currentStepIndex >= index ? "#fff" : "#adb5bd",
                          fontWeight: "600",
                          fontSize: "1.1rem",
                          border:
                            currentStepIndex >= index
                              ? "none"
                              : "2px solid #dee2e6",
                          boxShadow:
                            currentStepIndex === index
                              ? "0 0 0 4px var(--m-primary-subtle)"
                              : "none",
                        }}
                      >
                        {currentStepIndex > index ? (
                          <FiCheckCircle size={22} />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span
                        className="small text-center mt-1"
                        style={{
                          fontWeight: currentStepIndex >= index ? "600" : "500",
                          color:
                            currentStepIndex >= index ? "#212529" : "#868e96",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className="mx-2"
                        style={{
                          width: "60px",
                          height: "4px",
                          backgroundColor: "#e9ecef",
                          marginTop: "-28px",
                          borderRadius: "2px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: currentStepIndex > index ? "100%" : "0%",
                            backgroundColor: "var(--m-primary)",
                            transition: "width 0.4s ease-in-out",
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="card-body p-4 p-md-5">
              {/* STEP 1 */}
              {steps[currentStepIndex].id === "general" && (
                <div className="step-content animation-fade-in">
                  <h4 className="mb-4">Genel Bilgiler</h4>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <Input
                        label="Profil Adı"
                        value={profileData.name}
                        onChange={(v) =>
                          setProfileData((p) => ({ ...p, name: v }))
                        }
                        required
                        prefix={<FiShield />}
                        placeholder="Örn: Sipariş Ekibi, Üretim Operatörü"
                        disabled={profileData.isSystem}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label="Açıklama"
                        value={profileData.description}
                        onChange={(v) =>
                          setProfileData((p) => ({ ...p, description: v }))
                        }
                        placeholder="Profil açıklaması (opsiyonel)"
                      />
                    </div>

                    {!profileData.isSystem && (
                      <div className="col-12 mt-4">
                        <label
                          className="d-flex align-items-start gap-3 p-4 border rounded cursor-pointer transition-all"
                          style={{
                            backgroundColor: profileData.fullAccess
                              ? "#f8f9fa"
                              : "#fff",
                            borderColor: profileData.fullAccess
                              ? "var(--m-primary)"
                              : "#dee2e6",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={!!profileData.fullAccess}
                            style={{
                              width: "22px",
                              height: "22px",
                              accentColor: "var(--m-primary)",
                            }}
                            onChange={(e) => {
                              const v = e.target.checked;
                              setProfileData((p) => ({
                                ...p,
                                fullAccess: v,
                                pages: v ? [] : p.pages,
                                approvalAccess: v ? [] : p.approvalAccess,
                              }));
                            }}
                          />
                          <div>
                            <div className="fw-bold text-dark fs-6 mb-1">
                              Tam Sistem Erişimi
                            </div>
                            <div
                              className="text-muted small"
                              style={{ lineHeight: "1.5" }}
                            >
                              Kullanıcıya sistemdeki tüm sayfalara, üretim
                              hatlarına ve yönetim menülerine koşulsuz erişim
                              yetkisi verir. Özel yetkilendirme profilleri için
                              bu seçeneği kapalı tutun.
                            </div>
                          </div>
                        </label>
                      </div>
                    )}
                    {profileData.isSystem && (
                      <div className="col-12 mt-2">
                        <div className="alert alert-info py-2 small mb-0">
                          Bu bir sistem profilidir. Sadece açıklaması
                          düzenlenebilir.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2 */}
              {steps[currentStepIndex].id === "pages" && (
                <div className="step-content animation-fade-in">
                  <PageAccessSelector
                    pages={profileData.pages}
                    onChange={(pages) =>
                      setProfileData((p) => ({ ...p, pages }))
                    }
                    approvalAccess={profileData.approvalAccess}
                    onApprovalAccessChange={(approvalAccess) =>
                      setProfileData((p) => ({ ...p, approvalAccess }))
                    }
                    lockedFullAccess={
                      !!profileData.fullAccess || !!profileData.isSystem
                    }
                  />
                </div>
              )}

              {/* STEP: Approvals */}
              {steps[currentStepIndex].id === "approvals" && (
                <div className="step-content animation-fade-in">
                  <h4 className="mb-4">Onay Türü Yetkileri</h4>
                  <ApprovalAccessSelector
                    approvalAccess={profileData.approvalAccess || []}
                    onApprovalAccessChange={(approvalAccess) =>
                      setProfileData((p) => ({ ...p, approvalAccess }))
                    }
                  />
                </div>
              )}

              {/* STEP 3 */}
              {steps[currentStepIndex].id === "summary" && (
                <div className="step-content animation-fade-in">
                  <h4 className="mb-4">Profil Özeti</h4>

                  <div className="border rounded-3 shadow-sm bg-white overflow-hidden">
                    <div className="bg-light border-bottom px-4 py-3">
                      <h6 className="mb-0 text-dark fw-bold">Genel Bilgiler</h6>
                    </div>

                    <div className="px-4 py-3 border-bottom">
                      <div className="row align-items-center">
                        <div className="col-sm-4 text-muted fw-medium">
                          Profil Adı
                        </div>
                        <div className="col-sm-8 fw-bold text-dark fs-6">
                          {profileData.name || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3 border-bottom">
                      <div className="row align-items-center">
                        <div className="col-sm-4 text-muted fw-medium">
                          Açıklama
                        </div>
                        <div className="col-sm-8 text-dark">
                          {profileData.description || "-"}
                        </div>
                      </div>
                    </div>

                    <div className="px-4 py-3">
                      <div className="row align-items-center">
                        <div className="col-sm-4 text-muted fw-medium">
                          Erişim Türü
                        </div>
                        <div className="col-sm-8">
                          {profileData.fullAccess ? (
                            <span
                              className="badge bg-success-subtle text-success border border-success px-3 py-2 rounded-pill fw-bold"
                              style={{ fontSize: "13px" }}
                            >
                              Tam Sistem Erişimi
                            </span>
                          ) : (
                            <span
                              className="badge bg-primary-subtle text-primary border border-primary px-3 py-2 rounded-pill fw-bold"
                              style={{ fontSize: "13px" }}
                            >
                              Özel Sınırlandırılmış Yetki
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {!profileData.fullAccess && (
                      <>
                        <div className="bg-light border-top border-bottom px-4 py-3 mt-2">
                          <h6 className="mb-0 text-dark fw-bold">
                            Yetki Detayları
                          </h6>
                        </div>

                        <div className="px-4 py-3 border-bottom">
                          <div className="row align-items-center">
                            <div className="col-sm-4 text-muted fw-medium">
                              Görüntüleme Yetkisi
                            </div>
                            <div className="col-sm-8 text-dark">
                              <span className="fw-bold text-primary fs-5 me-2">
                                {readPageCount}
                              </span>
                              <span>sayfa görüntülenebilir.</span>
                            </div>
                          </div>
                        </div>

                        <div className="px-4 py-3 border-bottom">
                          <div className="row align-items-center">
                            <div className="col-sm-4 text-muted fw-medium">
                              Düzenleme Yetkisi
                            </div>
                            <div className="col-sm-8 text-dark">
                              <span className="fw-bold text-primary fs-5 me-2">
                                {writePageCount}
                              </span>
                              <span>sayfa düzenlenebilir.</span>
                            </div>
                          </div>
                        </div>

                        {hasApprovesPageAccess && (
                          <div className="px-4 py-3">
                            <div className="row align-items-center">
                              <div className="col-sm-4 text-muted fw-medium">
                                Onay Türleri
                              </div>
                              <div className="col-sm-8 text-dark">
                                <span className="fw-bold text-primary fs-5 me-2">
                                  {profileData.approvalAccess?.length || 0}
                                </span>
                                <span>
                                  adet onay sürecine müdahale yetkisi
                                  tanımlandı.
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions Footer */}
            <div className="card-footer bg-white p-4 border-top d-flex justify-content-between align-items-center">
              <Button
                appearance="danger-subtle"
                onClick={handlePrev}
                disabled={currentStepIndex === 0 || saving}
                icon={<FiArrowLeft />}
              >
                Geri
              </Button>

              {currentStepIndex < steps.length - 1 ? (
                <Button
                  appearance="primary-animated"
                  onClick={handleNext}
                  className="d-flex align-items-center gap-2"
                >
                  İleri <FiArrowRight />
                </Button>
              ) : (
                <Button
                  appearance="primary-animated"
                  onClick={handleSave}
                  loading={saving}
                  className="d-flex align-items-center gap-2"
                >
                  <FiSave size={16} />{" "}
                  {isEditing ? "Değişiklikleri Kaydet" : "Profili Oluştur"}
                </Button>
              )}
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            description={toast.description}
            appearance={toast.appearance}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>

      <style>{`
        .animation-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </AppPage>
  );
};

export default withAuth(ProfileForm);
